/*
 * types.ts — o contrato da Members API do portal Se Esforce (§4.3 do plano).
 *
 * Estes tipos são o acordo entre este SPA e o serviço que o WS13 implementará:
 * o client HTTP e o mock implementam exatamente esta superfície, então a UI
 * inteira (catálogo, compra, área logada) já nasce exercitando o contrato.
 *
 * Dinheiro é SEMPRE em centavos (`*Cents`), como no billing da plataforma: o
 * preço é dado do servidor, nunca calculado no cliente.
 */

/** Nível editorial do curso, mostrado no catálogo. */
export type CourseLevel = 'iniciante' | 'intermediario' | 'avancado';

/** Curso no catálogo público. `priceCents === 0` é um curso GRATUITO. */
export interface CourseSummary {
  slug: string;
  title: string;
  /** Uma frase de vitrine, em pt-BR. */
  summary: string;
  /** Ilustração de capa absoluta, ou null para o cartão sem imagem. */
  coverUrl: string | null;
  priceCents: number;
  lessonCount: number;
  durationMinutes: number;
  level: CourseLevel;
  /**
   * false = "em breve": o catálogo lista o curso, mas o detalhe só abre para o
   * admin ou para quem já tem acesso. A API de rollback não manda o campo — o
   * client normaliza para true ({@link ./http-client toCourseSummary}).
   */
  available: boolean;
  /** Slug da trilha ({@link Track}); null quando a API não informa. */
  track: string | null;
}

/** Trilha do catálogo: agrupa os cursos na vitrine (`GET /tracks`). */
export interface Track {
  slug: string;
  title: string;
  summary: string;
}

/** O catálogo composto pela vitrine: cursos + trilhas (as trilhas podem faltar no rollback). */
export interface CourseCatalog {
  courses: CourseSummary[];
  tracks: Track[];
}

/** Lição do sumário. Só a `sample` é legível sem inscrição ou compra. */
export interface CourseLesson {
  id: string;
  title: string;
  durationMinutes: number;
  /** Amostra gratuita: o conteúdo abre para qualquer visitante. */
  sample: boolean;
}

/** Detalhe público do curso, com o sumário completo (as amostras marcadas). */
export interface CourseDetail extends CourseSummary {
  /** Descrição longa em Markdown (o renderer do blog a exibe). */
  descriptionMarkdown: string;
  lessons: CourseLesson[];
}

/**
 * O direito de acesso ao conteúdo fechado de um curso. Nasce de uma inscrição
 * gratuita (`enrollment`) ou de uma compra confirmada (`purchase`) e é revogado
 * pelo estorno. O servidor valida um entitlement por par (conta, curso).
 */
export interface Entitlement {
  courseSlug: string;
  source: 'enrollment' | 'purchase';
  /** ISO-8601 da concessão. */
  grantedAt: string;
  /** A compra que o originou; null numa inscrição gratuita. */
  purchaseId: string | null;
}

/** Meio de pagamento de uma compra avulsa (sem recorrência). */
export type CoursePaymentMethod = 'pix' | 'card';

/**
 * O ciclo da compra: `pending → confirmed | refunded | expired`. `pending` é o
 * Pix aguardando baixa — ou o cartão ainda em análise no gateway — e o
 * entitlement SÓ nasce na confirmação; `confirmed` é pago (Pix baixado ou
 * cartão aprovado, este em geral na própria resposta da compra); `refunded` é o
 * estorno integral dentro da janela de arrependimento (com a revogação do
 * entitlement); `expired` é a cobrança que venceu sem pagamento. O servidor
 * relê e expira a pendente vencida a cada leitura.
 */
export type PurchaseStatus = 'pending' | 'confirmed' | 'refunded' | 'expired';

/**
 * A cobrança Pix de uma compra pendente — relida a cada GET, porque expira.
 * Pode faltar por um instante numa pendente recém-criada por outra requisição
 * (a cobrança ainda não existe no gateway): o polling a traz.
 */
export interface PixCharge {
  /** PNG do QR em base64 (sem prefixo data:), pronto para <img src={`data:...`}>. */
  encodedImage: string;
  /** BR Code copia-e-cola. */
  copyPaste: string;
  /** ISO-8601 do fim do prazo: o fim do dia do vencimento em Brasília — depois dele a compra expira. */
  expiresAt: string;
}

/** Uma compra avulsa de curso pago (a conta vê só as próprias). */
export interface CoursePurchase {
  id: string;
  courseSlug: string;
  method: CoursePaymentMethod;
  status: PurchaseStatus;
  amountCents: number;
  /** ISO-8601. */
  createdAt: string;
  /** ISO-8601 da baixa do pagamento; null enquanto pendente. */
  confirmedAt: string | null;
  /** ISO-8601 do estorno; null fora de `refunded`. */
  refundedAt: string | null;
  /** Presente só num Pix pendente; null nos demais estados e no cartão. */
  pix: PixCharge | null;
}

/*
 * A entrada da compra segue o `parse_purchase_request` da Members API
 * (members-api/src/members_api/domain.py), que é a fonte da verdade: ele recusa com
 * 422 e um código por regra. Campos numéricos vão com ou sem máscara, e o
 * servidor normaliza para dígitos. Nada disto é devolvido: as respostas mostram
 * só método e valor.
 */

/**
 * Quem paga, exigido em toda compra (Pix e cartão): o Asaas emite a cobrança
 * em nome dele. 422 `invalid_payer` para nome fora de 3–120 caracteres ou
 * documento sem dígitos verificadores válidos.
 */
export interface CoursePayerInput {
  name: string;
  /** CPF (11 dígitos) ou CNPJ (14). */
  cpfCnpj: string;
}

/**
 * O cartão de uma compra em `card`, com os dados do titular que o antifraude do
 * Asaas pede (CEP, número do endereço, telefone). 422 `invalid_card` quando
 * falta ou está fora do formato.
 */
export interface CourseCardInput {
  holderName: string;
  number: string;
  expiryMonth: string;
  /** AA ou AAAA. */
  expiryYear: string;
  ccv: string;
  /** CEP com 8 dígitos. */
  postalCode: string;
  /** 1 a 10 caracteres. */
  addressNumber: string;
  /** Telefone com DDD, 10 ou 11 dígitos. */
  phone: string;
}

/** A intenção de compra: meio, pagador e (em `card`) o cartão. Nunca um valor. */
export interface CoursePurchaseInput {
  method: CoursePaymentMethod;
  payer: CoursePayerInput;
  card?: CourseCardInput | null;
}

/**
 * O conteúdo de uma lição, entregue SÓ com o entitlement validado pelo servidor
 * (ou a todos, quando a lição é amostra). As mídias vêm como URLs assinadas e
 * curtas — nunca um link permanente.
 */
export interface LessonContent {
  lessonId: string;
  title: string;
  /** Corpo da lição em Markdown (o renderer do blog o exibe). */
  contentMarkdown: string;
  /**
   * URL pré-assinada (S3, 10 minutos) do vídeo da lição, ou null numa lição
   * só-texto. Vale para abrir na hora; vencida, reabrir a lição pede outra.
   */
  videoUrl: string | null;
}

/* ---- Configuração do site (topbar, rodapé, equipe) ----------------------------------- */

/** Onde o link aparece: topbar ou uma das colunas/barras do rodapé. */
export type SiteLinkArea =
  | 'navbar'
  | 'footer_platform'
  | 'footer_elsewhere'
  | 'footer_social'
  | 'footer_legal';

/** Ícones que o rodapé sabe desenhar (vazio = sem ícone). */
export type SiteLinkIcon =
  | ''
  | 'youtube'
  | 'instagram'
  | 'linkedin'
  | 'github'
  | 'globe'
  | 'send'
  | 'message-circle'
  | 'newspaper'
  | 'mail';

/**
 * Um link editável pelo admin. O servidor só grava caminho interno `/…`,
 * `https://` ou `mailto:`; a UI ainda confere a forma antes de renderizar
 * (`classifyHref`, em `lib/site`).
 */
export interface SiteLink {
  id: string;
  area: SiteLinkArea;
  label: string;
  href: string;
  /** Linha mono abaixo do rótulo ("Onde mais"). */
  subtitle: string;
  icon: SiteLinkIcon;
  /** Selo curto ao lado do rótulo (ex.: NOVO). */
  badge: string;
  enabled: boolean;
  position: number;
}

export interface SiteLinks {
  navbar: SiteLink[];
  footerPlatform: SiteLink[];
  footerElsewhere: SiteLink[];
  footerSocial: SiteLink[];
  footerLegal: SiteLink[];
}

/** A chave de cada área em {@link SiteLinks} (`GET /site` usa camelCase; `/admin/site`, a área). */
export const SITE_LINK_AREA_KEYS: Readonly<Record<SiteLinkArea, keyof SiteLinks>> = {
  navbar: 'navbar',
  footer_platform: 'footerPlatform',
  footer_elsewhere: 'footerElsewhere',
  footer_social: 'footerSocial',
  footer_legal: 'footerLegal',
};

/** O que todo visitante vê. O convite da comunidade NÃO vem aqui. */
export interface SiteSettings {
  brandDescription: string;
  /** Só dígitos (DDI + DDD + número) ou vazio. */
  whatsappPhone: string;
  copyrightHolder: string;
  /** Nome jurídico do rodapé; vazio não renderiza. */
  legalName: string;
  contactEmail: string;
  communityLabel: string;
  youtubeUrl: string;
}

/** Alguém da equipe do "Sobre"; a foto é um arquivo do próprio site (ou iniciais). */
export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  photoPath: string;
  linkLabel: string;
  linkUrl: string;
  position: number;
  enabled: boolean;
}

/** `GET /site`: só o ativo, na ordem de exibição. */
export interface SiteConfig {
  settings: SiteSettings;
  links: SiteLinks;
  team: TeamMember[];
}

export interface AdminSiteSettings extends SiteSettings {
  communityUrl: string;
}

/** `GET /admin/site`: tudo, inclusive desativados, por área. */
export interface AdminSiteConfig {
  settings: AdminSiteSettings;
  links: Record<SiteLinkArea, SiteLink[]>;
  team: TeamMember[];
}

/** O admin manda só as chaves da aba que salvou; o servidor mescla. */
export type SiteSettingsInput = Partial<AdminSiteSettings>;

export interface SiteLinkInput {
  label: string;
  href: string;
  subtitle?: string;
  icon?: SiteLinkIcon;
  badge?: string;
  enabled?: boolean;
}

/** Substitui a área inteira, na ordem enviada (até 20 links). */
export interface SiteLinksInput {
  area: SiteLinkArea;
  links: SiteLinkInput[];
}

export interface TeamMemberInput {
  name: string;
  role?: string;
  bio?: string;
  photoPath?: string;
  linkLabel?: string;
  linkUrl?: string;
  enabled?: boolean;
}

/** Substitui a equipe inteira, na ordem enviada (até 12 pessoas). */
export interface TeamInput {
  members: TeamMemberInput[];
}

/* ---- Área do membro -------------------------------------------------------------------- */

/**
 * As preferências do membro guardadas pela Members API. O nome não está aqui:
 * ele vive na sessão (`useBlogAuth().user.displayName`).
 */
export interface MemberProfile {
  email: string | null;
  /** Só dígitos, ou vazio quando não informado. */
  whatsapp: string;
  emailNewLessons: boolean;
  emailAnnouncements: boolean;
  notifyYoutube: boolean;
  isAdmin: boolean;
  /** A conta tem senha (não só Google): decide "Criar senha" × "Alterar senha". */
  hasPassword: boolean;
}

/** Só as chaves enviadas mudam. 422 `invalid_profile` fora da regra. */
export type MemberProfileInput = Partial<
  Pick<MemberProfile, 'whatsapp' | 'emailNewLessons' | 'emailAnnouncements' | 'notifyYoutube'>
>;

/** Uma lição aberta, da mais recente para a mais antiga (até 100). */
export interface HistoryItem {
  courseSlug: string;
  /** O slug quando o curso saiu do catálogo. */
  courseTitle: string;
  lessonId: string;
  /** O id quando a lição saiu do curso. */
  lessonTitle: string;
  /** ISO-8601. */
  lastViewedAt: string;
}

export interface CourseResource {
  id: string;
  title: string;
  url: string;
  kind: 'link';
}

/** Os materiais de um curso que o membro tem. */
export interface CourseResources {
  courseSlug: string;
  courseTitle: string;
  resources: CourseResource[];
}

/** O convite da comunidade: só com uma compra (ou o papel admin). */
export interface CommunityAccess {
  hasAccess: boolean;
  /** null sem acesso, ou com acesso e o convite ainda não publicado. */
  url: string | null;
  /** O nome do canal (WhatsApp, Discord…). */
  label: string;
}

/** Um aviso do sino. `publishedAt` null = rascunho (só o admin vê). */
export interface SiteNotification {
  id: string;
  title: string;
  body: string;
  href: string;
  /** ISO-8601. */
  publishedAt: string | null;
}

/** As publicadas mais recentes (até 20) e quantas o membro ainda não viu. */
export interface NotificationsPage {
  unseenCount: number;
  items: SiteNotification[];
}

/** Sem `id` cria; com `id` edita. `publish: false` deixa (ou volta) a rascunho. */
export interface NotificationInput {
  id?: string;
  title: string;
  body: string;
  href: string;
  publish: boolean;
}

/**
 * A janela de arrependimento: estorno integral automatizado até este número de
 * dias APÓS a confirmação do pagamento, com revogação do entitlement. Fora dela
 * não há estorno. O servidor é a fonte da verdade (o código de erro é
 * `refund_window_expired`); a UI repete a regra ANTES da confirmação da compra.
 */
export const COURSE_REFUND_WINDOW_DAYS = 7;
